<template>
  <div class="pa-6">
    <h3 class="text-left mb-1 text-primary">GUI Playground</h3>
    <p class="text-caption mb-4">
      Puerto de <code>index-gui.html</code> (harness de testing de AlisonGUI). El widget se monta abajo a la derecha de la pantalla.
    </p>

    <!-- Orden de secciones igual al de index.html: Acciones primero (arriba
         de todo, como quedó tras ALILIBS-368), después los controles
         generales, providers, showText2SignFormat, paleta, resultado y por
         último ancho del carrusel + sus propios botones de prueba. -->
    <h4 class="mb-2">Acciones</h4>
    <div class="test-buttons mb-6">
      <v-btn size="small" variant="outlined" @click="testMode('sign')">Sign</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('select')">Select</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('renew')">Renew</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('authenticate')">Authenticate</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('list')">List</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('test')">Test</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('detect')">Detect</v-btn>
      <v-btn size="small" variant="outlined" @click="testMode('detect-and-install')">Detect & Install</v-btn>
      <v-btn size="small" variant="outlined" @click="testDirectSign">DirectSign</v-btn>
      <v-btn size="small" variant="outlined" @click="testUnmount">Test Unmount</v-btn>
      <v-btn size="small" variant="outlined" @click="sentMessageToPlugin">Enviar desde Web</v-btn>
      <v-btn size="small" variant="outlined" @click="testMessageToPlugin">Call plugin</v-btn>
    </div>

    <h4 class="mb-2">Controles generales</h4>
    <v-row dense class="align-center mb-2">
      <v-col cols="6" sm="3">
        <v-select
          v-model="selectedLanguage"
          :items="[{ title: 'Español', value: 'es' }, { title: 'English', value: 'en' }]"
          label="Idioma"
          density="compact"
          hide-details
          @update:modelValue="persistLanguage"
        ></v-select>
      </v-col>
      <v-col cols="6" sm="3">
        <v-select
          v-model="selectedThemeName"
          :items="['default', 'theme1', 'theme2', 'theme3']"
          label="Tema"
          density="compact"
          hide-details
          @update:modelValue="persistSelectedTheme"
        ></v-select>
      </v-col>
      <v-col cols="6" sm="3">
        <v-select
          v-model="certFilterKey"
          :items="[
            { title: 'Ninguno', value: 'none' },
            { title: 'Vigentes', value: 'valid' },
            { title: 'Expirados', value: 'expired' },
            { title: 'Vacío', value: 'empty' },
          ]"
          label="Filtro"
          density="compact"
          hide-details
          @update:modelValue="persistCertFilter"
        ></v-select>
      </v-col>
    </v-row>
    <div class="d-flex flex-wrap ga-4 align-center mb-6">
      <v-switch
        v-model="simulateOnValidate"
        label="Simular onValidate"
        density="compact"
        hide-details
        @update:modelValue="persistSimulateOnValidate"
      ></v-switch>
      <v-switch
        v-model="showText2SignFormatEnabled"
        label="Mostrar texto a firmar"
        density="compact"
        hide-details
        @update:modelValue="persistShowText2SignFormatEnabled"
      ></v-switch>
      <v-btn size="small" variant="outlined" @click="clearSavedCredentials">Borrar claves guardadas</v-btn>
    </div>

    <GuiPlaygroundProviders
      ref="providersPanel"
      @test-file="onTestFile"
      @result="onProviderResult"
    ></GuiPlaygroundProviders>

    <GuiPlaygroundValidateDialog
      ref="validateDialog"
      @result="onProviderResult"
    ></GuiPlaygroundValidateDialog>

    <h4 v-if="showText2SignFormatEnabled" class="mb-2">showText2SignFormat</h4>
    <div v-if="showText2SignFormatEnabled" class="mb-6">
      <div v-for="group in formatGroups" :key="group.label" class="test-buttons format-row">
        <span class="format-label">{{ group.label }}</span>
        <v-btn
          v-for="fmt in group.variants"
          :key="fmt"
          size="x-small"
          density="compact"
          :variant="fmt === selectedTextToSignFormat ? 'flat' : 'outlined'"
          :color="fmt === selectedTextToSignFormat ? 'primary' : undefined"
          @click="selectTextToSignFormat(fmt)"
        >{{ fmt }}</v-btn>
      </div>
    </div>

    <h4 class="mb-2">Palette</h4>
    <div class="palette-grid mb-4">
      <div
        v-for="p in palettes"
        :key="p.id"
        class="palette-card"
        :class="{ active: p.id === activePaletteId }"
        @click="selectPalette(p.id)"
      >
        <div class="palette-swatch">
          <span :style="{ background: p.palette?.primary?.dark || '#6B7280' }"></span>
          <span :style="{ background: p.palette?.primary?.main || '#9CA3AF' }"></span>
          <span :style="{ background: p.palette?.primary?.light || '#D1D5DB' }"></span>
          <span :style="{ background: p.palette?.error?.main || '#fc4336' }"></span>
          <span :style="{ background: p.palette?.success?.main || '#2e7d32' }"></span>
        </div>
        <div class="palette-label">{{ p.label }}</div>
      </div>
    </div>

    <div class="result-box mb-6" v-html="resultHtml"></div>

    <h4 class="mb-2">Ancho del carrusel</h4>
    <div class="d-flex flex-wrap ga-2 align-center mb-2">
      <v-btn
        v-for="w in carouselWidths"
        :key="w.label"
        size="small"
        :variant="carouselWidth === w.value ? 'flat' : 'outlined'"
        @click="setCarouselWidth(w.value)"
      >{{ w.label }}</v-btn>
      <v-btn size="small" variant="outlined" @click="testCarousel">Test Carousel</v-btn>
      <v-btn size="small" variant="outlined" @click="testCarouselMock">Test Carousel (mock, no hardware)</v-btn>
      <v-btn size="small" variant="outlined" @click="testCarouselTiming">Test Carousel (ALILIBS-331 timing repro)</v-btn>
    </div>
    <div
      ref="carouselContainer"
      class="carousel-container"
      :style="{ width: typeof carouselWidth === 'number' ? carouselWidth + 'px' : carouselWidth }"
    ></div>
  </div>
</template>

<script>
import * as AlisonJS from 'AlisonJS';
import alisonGUI from '@/services/alison_gui.js';
import enviroment from '@/enviroment.js';
import GuiPlaygroundProviders from './GuiPlaygroundProviders.vue';
import GuiPlaygroundValidateDialog from './GuiPlaygroundValidateDialog.vue';

// Puerto de CERT_FILTER_OPTIONS de alison-gui/index.html (ver
// docs/config-reference.md#filters en alison-gui: array de objetos, OR entre
// elementos y AND entre claves de un mismo objeto).
const CERT_FILTER_OPTIONS = {
  none: [],
  valid: [{ windowValidity: '0,*' }],
  expired: [{ windowValidity: '*,-1' }],
  // Ningún certificado real tiene este status: sirve para probar el caso
  // "0 resultados" (todo queda filtered:true).
  empty: [{ status: ['__ninguno__'] }],
};

const PALETTES = [
  { id: 'default', label: 'Default (no palette)', palette: null },
  {
    id: 'green', label: 'Forest green',
    palette: {
      primary: { main: '#387056', light: '#60a080', dark: '#2a5441' },
      secondary: { main: '#C8D6DA' },
      error: { main: '#fc4336' },
      warning: { main: '#CE9F1E' },
      success: { main: '#2e7d32' },
    },
  },
  {
    id: 'blue', label: 'Corporate blue',
    palette: {
      primary: { main: '#1A3C6E', light: '#2E5FA3', dark: '#112B52' },
      secondary: { main: '#E8EEF5' },
      error: { main: '#C62828' },
      warning: { main: '#E65100' },
      success: { main: '#1B5E20' },
    },
  },
  {
    id: 'indigo', label: 'Indigo',
    palette: {
      primary: { main: '#3730A3', light: '#6366F1', dark: '#1E1B72' },
      secondary: { main: '#EEF2FF' },
      error: { main: '#B91C1C' },
      warning: { main: '#D97706' },
      success: { main: '#15803D' },
    },
  },
  {
    id: 'teal', label: 'Teal',
    palette: {
      primary: { main: '#0F766E', light: '#14B8A6', dark: '#0D4F4A' },
      secondary: { main: '#F0FDFA' },
      error: { main: '#DC2626' },
      warning: { main: '#F59E0B' },
      success: { main: '#16A34A' },
    },
  },
  {
    id: 'crimson', label: 'Crimson',
    palette: {
      primary: { main: '#9B1C1C', light: '#DC2626', dark: '#6B0F0F' },
      secondary: { main: '#FEF2F2' },
      error: { main: '#7F1D1D' },
      warning: { main: '#B45309' },
      success: { main: '#166534' },
    },
  },
  {
    id: 'slate', label: 'Slate',
    palette: {
      primary: { main: '#334155', light: '#64748B', dark: '#1E293B' },
      secondary: { main: '#F1F5F9' },
      error: { main: '#DC2626' },
      warning: { main: '#D97706' },
      success: { main: '#16A34A' },
    },
  },
  {
    id: 'violet', label: 'Violet',
    palette: {
      primary: { main: '#6D28D9', light: '#8B5CF6', dark: '#4C1D95' },
      secondary: { main: '#F5F3FF' },
      error: { main: '#DC2626' },
      warning: { main: '#D97706' },
      success: { main: '#16A34A' },
    },
  },
];

const DATA_TO_SIGN = "PGgzIHN0eWxlPSJjb2xvcjogIzMzMzMzMzsgZm9udC13ZWlnaHQ6IGJvbGQiPkRhdG9zIGRlIGxhIFRyYW5zZmVyZW5jaWE8L2gzPgoKPHRhYmxlIHN0eWxlPSJib3JkZXI6IDFweCBzb2xpZCAjY2NjY2NjOyB3aWR0aDogMTAwJTsgYm9yZGVyLWNvbGxhcHNlOiBjb2xsYXBzZSI+CgogIDx0ciBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjogI2Y1ZjVmNSI+CgogICAgPHRoIHN0eWxlPSJ0ZXh0LWFsaWduOiBsZWZ0OyBwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPkNhbXBvPC90aD4KCiAgICA8dGggc3R5bGU9InRleHQtYWxpZ246IGxlZnQ7IHBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYyI+VmFsb3I8L3RoPgoKICA8L3RyPgoKICA8dHI+CgogICAgPHRkIHN0eWxlPSJwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPjxiPkN1ZW50YSBvcmlnZW48L2I+PC90ZD4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYzsgd2hpdGUtc3BhY2U6IG5vd3JhcCI+MC0wMDAtMDEyMzQ1NjctODwvdGQ+CgogIDwvdHI+CgogIDx0cj4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYyI+PGI+TW9udG88L2I+PC90ZD4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYzsgd2hpdGUtc3BhY2U6IG5vd3JhcCI+JCAxLjUwMC4wMDA8L3RkPgoKICA8L3RyPgoKICA8dHI+CgogICAgPHRkIHN0eWxlPSJwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPjxiPkJlbmVmaWNpYXJpbzwvYj48L3RkPgoKICAgIDx0ZCBzdHlsZT0icGFkZGluZzogNnB4OyBib3JkZXI6IDFweCBzb2xpZCAjY2NjY2NjIj5KdWFuIFAmZWFjdXRlO3JleiBMJm9hY3V0ZTtwZXo8L3RkPgoKICA8L3RyPgoKPC90YWJsZT4KCjxicj4KCjxwIHN0eWxlPSJjb2xvcjogI2NjMDAwMCI+PGVtPkF1dG9yaXpvIGxhIG9wZXJhY2nDs24gaW5kaWNhZGEgYXJyaWJhIGFsIGZpcm1hciBkaWdpdGFsbWVudGUgZXN0YSBzb2xpY2l0dWQuPC9lbT48L3A+";

// Mismo token de prueba (entorno desa) que ya usa TestGui.vue para el provider desktop.
const DESKTOP_ACCESS_TOKEN = "eyJ2ZXJzaW9uIjo0LCJ2MSI6eyJpZHgiOiI5LUxpY2Vuc2UiLCJ2YWx1ZSI6IjMvK0RKMmsxQS9yWjQxL0hlWllYS00ydG1XazE4cDM4RXg0WDdWTWZlYXhpS3dWUHZVRTZzaUh1aHplZThHU1RMR2FwUWRXaFJRNzlyQXRqeWpRWkYxeC83Rm1ZcERzVExnTTQzdDRlV2dMV2NaclQ2Sy9uRGwvc1ltRGtLZjBjWDFtc3JHbkM4RWNQNk9KQW04RGlhUG5ZVlR0ZUpaOVEzbVNJeDk2OGU0cnpJNlF6YUh2L09FQnVmUHhMbTdpMjhQS3JBbHdSS3BQZTFVc3c4eG45NjMxRlJWc1JxaEk1WGFoamRoV2RWQnRkRmg1bE51Y1BUUDVEbFQzdkJIZy94VmV5TllOdVcvRWFobUZiS2RSRS9IRmdEOFN0cnVPTTVvNXJLSHptc2xPbnJFSi8yQkdEa1AxcnF3Nm0wZnlZUTR6K2RpYzdlTHhLWnRsNUNGWGhBR0dLOEdIYXVJVGU5RWUrQUJjN3c1WC8zZkJKVDI4VTgyOEo1V0dOZ0t6TVo5akdsbzVLNVgxOEh1cHVvZ1BGSm9nVjkyV2llbmR5c2JXMmRiZXltcVNINDBTN2kvcDdLb2pRZ2ZyNWtveHl1ZFV1cmgvK1NuYXhYOEczMTF2THU0OUNIalpmdCtycXkvRFlUZFdRS3pYYTFHanZMME5zQkIyNTFRU01OdDdoLzFSYjJkdnNMVk5HcnNKR3BjNFhMWEx1a0J6V1pXSW4zeWd1cDZUMlR0SnhzZCtPSWhtVkxhZldNbHhNME03diJ9LCJ2MiI6eyJpZHgiOiI5LUxpY2Vuc2UiLCJ2YWx1ZSI6ImV5SmhiR2NpT2lKU1V6STFOaUo5LmV5SnBZWFFpT2pFM05EUXpNVEkyTURVc0ltVjRjQ0k2TVRnME56Y3pOakF3TUN3aVkyOXRjR0Z1ZVU1aGJXVWlPaUpEWlhKMGFWTjFjaUJUTGtFdUlpd2lZV3hzYjNkbFpFOXlhV2RwYm5NaU9sc2lLaTVqWlhKMGFYTjFjaTVqYjIwaUxDSXFMbU5sY25ScGMzVnlMbTVsZENJc0lpb3VZMlZ5ZEdsemRYSXVhVzVtYnlJc0lpb3VkbVZ5YVhOMWNpSXNJaW91ZEc5d2RISjFjM1F1WTI5dExtRnlJaXdpS2k1elpXTjFjbVZrYjJNdWJHRWlMQ0pqYUhKdmJXVXRaWGgwWlc1emFXOXVPaTh2YTNCcWJHRm5aR3B3WVdscGJXNWxZMlJpWkdOdVpHcGphV3B2WVd4aWJHRWlMQ0l4TWpjdU1DNHdMakU2T0RBd015SXNJakV5Tnk0d0xqQXVNVG80TURBMElpd2liRzlqWVd4b2IzTjBPamd3TURNaUxDSnNiMk5oYkdodmMzUTZPREF3TkNJc0lteHZZMkZzYUc5emRDNWpaWEowYVhOMWNpNXVaWFE2T0RBNE1DSmRmUS5Gc09hel9LRWJ2WFh1dkZldXVCWExNYU42dVNjcXF2akt2Vl94dnhrUjQ2eGxuMDZyWnhTby14NElHdjcxajN0dHpERWVXUkQzUTZyV1lfeTViajR3LS0wN1dGRnM4eE8zdVpDUVdfclh3enk5TTJ2bkxIRGdtc0IyNUFPYkhTUVY3QW9iNzRZYTJRcjdlMVFQZldGallpUjlMV3hlVGV0a2dpWTluV2U2MFRoNG5sZC1mbGJSbjVVVnExQ0hqTVBiR3llc0hmSktXSkxZN001NlRIN0pKTno2Q0FNTFIwQkRlSmpodUhJN2ktMkJ2MjRzMmREMFV4Q3ZXU1RJM1piODkyc2xQMVBFdHROWGpvU2tVWVotSlU0WU1GN2tSVDZabnZmR3ZYSjdPQXdnTTFvMFRjR0Q2NGt0bXVySk00dzJoVGZXUDd3SGxXNjFoNE1aamVRcmcifX0="; // gitleaks:allow — token de prueba entorno desa, mismo valor ya presente en TestGui.vue

// Token de prueba usado solo por testDirectSign (entorno desa, cliente "glock-usage").
const DIRECT_SIGN_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJnbG9jay11c2FnZSIsImF1ZCI6Imdsb2NrLXVzYWdlIiwibmJmIjoxNzQ5OTcxMjIyLCJzY29wZSI6WyJzZWF0OjE4NTE4NjAwQEFDTUUiXSwiaXNzIjoiaHR0cDovL2Rlc2EtYWxpc29uc2VydmVyLmNlcnRpc3VyLmNvbSIsImV4cCI6MTc0OTk3NDgyMiwiaWF0IjoxNzQ5OTcxMjIyLCJqdGkiOiI1NmNlNTRkZC03MzkzLTQ0NWItOWM5OS0yODI4OGE5MDlhNzEiLCJhdXRob3JpdGllcyI6WyJVU0FHRSJdLCJ0ZW5hbnQiOiIiLCJjbGllbnRfaWQiOiJnbG9jay11c2FnZSJ9.Pg19IxABMFvqYyATDanjBBqmH65z0AEcMtnMT70pbqQ"; // gitleaks:allow — token de prueba entorno desa (cliente glock-usage), ya expirado

export default {
  components: {
    GuiPlaygroundProviders,
    GuiPlaygroundValidateDialog,
  },
  data: () => ({
    resultHtml: '<p>Results will appear here...</p>',
    activePaletteId: localStorage.getItem('activePaletteId') || 'default',
    palettes: PALETTES,
    carouselWidth: '100%',
    carouselWidths: [
      { label: 'Compacto (360px)', value: 360 },
      { label: 'Mediano (600px)', value: 600 },
      { label: 'Ancho (900px)', value: 900 },
      { label: 'Completo (100%)', value: '100%' },
    ],
    formatGroups: [
      { label: 'html', variants: ['html', 'html:compact', 'html:regular', 'html:wide'] },
      { label: 'text', variants: ['text', 'text:compact', 'text:regular', 'text:wide'] },
      { label: 'base64', variants: ['base64', 'base64:compact', 'base64:regular', 'base64:wide'] },
    ],
    selectedTextToSignFormat: localStorage.getItem('textToSignFormat') || 'html',
    selectedLanguage: localStorage.getItem('language') || 'es',
    selectedThemeName: (() => {
      const stored = localStorage.getItem('theme');
      return ['default', 'theme1', 'theme2', 'theme3'].includes(stored) ? stored : 'theme1';
    })(),
    certFilterKey: (() => {
      const stored = localStorage.getItem('certFilterKey');
      return CERT_FILTER_OPTIONS[stored] ? stored : 'valid';
    })(),
    simulateOnValidate: localStorage.getItem('simulateOnValidate') === 'true',
    showText2SignFormatEnabled: localStorage.getItem('showText2SignFormatEnabled') !== 'false',
  }),
  methods: {
    persistLanguage() {
      localStorage.setItem('language', this.selectedLanguage);
    },
    persistSelectedTheme() {
      localStorage.setItem('theme', this.selectedThemeName);
    },
    persistCertFilter() {
      localStorage.setItem('certFilterKey', this.certFilterKey);
    },
    persistSimulateOnValidate() {
      localStorage.setItem('simulateOnValidate', String(this.simulateOnValidate));
    },
    persistShowText2SignFormatEnabled() {
      localStorage.setItem('showText2SignFormatEnabled', String(this.showText2SignFormatEnabled));
    },
    getActiveCertFilters() {
      return CERT_FILTER_OPTIONS[this.certFilterKey] || [];
    },
    // Combina nombre de tema + paleta activa en un único `gui.theme`, igual
    // que buildThemeConfig(name) en alison-gui/index.html.
    buildThemeConfig() {
      const theme = { name: this.selectedThemeName };
      const active = this.palettes.find((p) => p.id === this.activePaletteId);
      if (active?.palette) theme.palette = active.palette;
      return theme;
    },
    selectPalette(paletteId) {
      this.activePaletteId = paletteId;
      localStorage.setItem('activePaletteId', paletteId);
    },
    onTestFile(mode) {
      const config = this.getBasicConfig({ allProviders: true });
      config.mode = mode;
      config.providers = { file: config.providers.file };
      alisonGUI.mount(config);
    },
    onProviderResult(message) {
      this.resultHtml = `<p>${message}</p>`;
    },
    clearSavedCredentials() {
      this.$refs.providersPanel?.clearSavedCredentials();
      this.resultHtml = '<p>Claves guardadas borradas.</p>';
    },
    // Cablea `onValidate` en los providers que lo aceptan (server y tad) y
    // QUITA su `token`: la GUI sólo ofrece "Completar" (que llama a
    // onValidate) cuando el provider falla con SERVER_TOKEN_UNDEFINED, lo que
    // ProviderManager tira si `args.token` viene vacío. El éxito de la
    // simulación devuelve de vuelta estos mismos tokens (ver
    // GuiPlaygroundValidateDialog.vue), como ProviderManager.startValidation
    // espera.
    attachValidateSimulation(config) {
      if (!this.simulateOnValidate) return config;
      const tokens = {
        serverToken: config.providers?.server?.token || null,
        tadToken: config.providers?.tad?.token || null,
      };
      ['server', 'tad'].forEach((provId) => {
        const provider = config.providers?.[provId];
        if (!provider) return;
        provider.onValidate = () => this.$refs.validateDialog.open(tokens);
        delete provider.token;
        delete provider.accessToken;
        delete provider.onGetToken;
      });
      return config;
    },
    getBasicConfig(opts) {
      const config = {
        mode: 'sign',
        gui: {
          theme: this.buildThemeConfig(),
          buttonReverse: false,
          logo: './acme/logo.jpg',
          header: '',
          showText2SignFormat: this.showText2SignFormatEnabled ? this.selectedTextToSignFormat : '',
          language: { key: this.selectedLanguage },
          wizardUrl: 'https://desa-desktop.certisur.com/acme',
        },
        providers: {
          desktop: {
            urls: ['https://127.0.0.1:8004', 'https://localhost:8004', 'http://127.0.0.1:8003'],
            accessToken: DESKTOP_ACCESS_TOKEN,
            timeout: 14500,
          },
          ...this.$refs.providersPanel?.getProvidersConfig(),
        },
        selector: {
          thumbPrint: '0D910ABCCB75485D3156E07CA73B3522474D9CBE',
          profileName: 'MACOS',
          keyStoreId: 'MACOS',
          provider: 'desktop',
        },
        filters: this.getActiveCertFilters(),
        signature: {
          dataToSign: [DATA_TO_SIGN],
          options: {
            algorithm: 'SHA256withRSA',
            format: 'PKCS1',
            textFormat: 'base64',
            extraParams: '',
          },
        },
        onConfirm: (result) => {
          console.debug('AD-SDK>> Alison-GUI:SUBMIT:', result);
          this.resultHtml = `<p>${JSON.stringify(result)}</p>`;
        },
        onCancel: (result) => {
          console.debug('AD-SDK>> Alison-GUI:CANCEL:', result);
          this.resultHtml = `<p>${JSON.stringify(result)}</p>`;
        },
      };

      // Sin `opts.allProviders`, respeta los checkboxes de "Providers": saca
      // del config cualquier provider no habilitado (incluido desktop).
      if (!opts || !opts.allProviders) {
        const enabled = this.$refs.providersPanel?.enabled || {};
        Object.keys(config.providers).forEach((provId) => {
          if (!enabled[provId]) delete config.providers[provId];
        });
      }

      return this.attachValidateSimulation(config);
    },
    testMode(mode) {
      const config = this.getBasicConfig();
      config.mode = mode;
      alisonGUI.mount(config);
    },
    // Puerto de selectTextToSignFormat(fmt) de index.html: sólo fija el
    // formato seleccionado (persistido + resaltado en el botón activo). El
    // mount real pasa por los botones de "Acciones" (Sign, etc.), que leen
    // este valor vía getBasicConfig().
    selectTextToSignFormat(fmt) {
      this.selectedTextToSignFormat = fmt;
      localStorage.setItem('textToSignFormat', fmt);
    },
    testDirectSign() {
      const config = this.getBasicConfig();
      config.mode = 'direct-sign';
      config.gui.theme.name = 'theme1';
      config.providers.server = {
        token: DIRECT_SIGN_TOKEN,
        tenant: 'acme',
        userType: 'user',
        url: enviroment.alisonServerApiUrl,
        urlUser: enviroment.webauthnUserPanelUrl,
      };
      config.selector = {
        cert: {
          issuerDN: 'CN=TEST CertiSur Class B Shared CA - G1,OU=Pilot Environment,O=CertiSur S.A.,C=AR',
          serialNumber: '74264ECC722D9D84F7527E9817E17ED0',
          subjectDN: 'CN=armando Prueba,OU=REL -,OU=ID - 101010,OU=Banking Services,O=Acme Inc',
          validFrom: '20241021000000Z',
          validTo: '20241115235959Z',
        },
        PIN: { presence: 'true', format: 'A', label: 'Insert the pin value', description: '' },
        OTP: { presence: 'true' },
        SMS: { presence: 'true' },
        EMAIL: { presence: 'true' },
        authMode: 'explicit',
        multisign: 2,
        credentialId: 'D1C0-3FB8',
        provider: 'server',
        profileName: 'D1C0-3FB8 : Armando Carratala Egea',
      };
      alisonGUI.mount(config);
    },
    testUnmount() {
      try {
        alisonGUI.unmount();
      } catch (e) {
        console.debug(e);
      }
    },
    setCarouselWidth(width) {
      this.carouselWidth = width;
    },
    async testCarousel() {
      const container = this.$refs.carouselContainer;
      container.innerHTML = '';
      try {
        // allProviders: true — este test instancia AlisonJS.Desktop directo,
        // sin pasar por AlisonGUI.mount(), así que necesita el bloque
        // `desktop` de la config exista más allá del checkbox de "Providers".
        const config = this.getBasicConfig({ allProviders: true });
        const desktopCfg = config.providers.desktop;
        const desktop = new AlisonJS.Desktop(desktopCfg.urls, desktopCfg.timeout);
        await desktop.initialize({ accessToken: desktopCfg.accessToken, timeout: desktopCfg.timeout });
        const { certificates } = await desktop.certificateList([]);
        certificates.forEach((cert) => { cert.provider = 'desktop'; });

        alisonGUI.renderCertificatesList(certificates, container, { gui: config.gui });

        const shadow = container.shadowRoot;
        await new Promise((resolve) => {
          if (shadow.querySelector('.embla')) return resolve();
          const observer = new MutationObserver(() => {
            if (shadow.querySelector('.embla')) {
              observer.disconnect();
              resolve();
            }
          });
          observer.observe(shadow, { childList: true, subtree: true });
        });
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        console.error(e);
        this.resultHtml = `<p>Carousel error: ${e.message || e}</p>`;
      }
    },
    buildMockCertificates(count) {
      const certs = [];
      for (let i = 0; i < count; i++) {
        const left = 656 - i * 11;
        certs.push({
          cert: {},
          validity: {
            from: '2024-01-01T00:00:00Z',
            until: '2026-10-23T00:00:00Z',
            total: 1000,
            left,
          },
          subject: {
            CN: `Titular de prueba ${i + 1}`,
            O: 'Acme Inc',
            OU: 'Banking Services',
            EMAILADDRESS: `titular${i + 1}@acme.com`,
            DN: `CN=Titular de prueba ${i + 1},O=Acme Inc`,
          },
          issuer: {
            CN: 'TEST CertiSur Class B Shared CA - G1',
            O: 'CertiSur S.A.',
            DN: 'CN=TEST CertiSur Class B Shared CA - G1,O=CertiSur S.A.',
          },
          status: 'valid',
          provider: 'desktop',
          profile: {
            keyStoreType: 'MACOS',
            keyStoreFriendlyName: 'MACOS',
            friendlyName: `perfil-${i + 1}`,
          },
          serialNumberH: `74:26:4E:CC:${(1000 + i).toString(16).toUpperCase()}`,
          filtered: false,
        });
      }
      return certs;
    },
    testCarouselMock() {
      const container = this.$refs.carouselContainer;
      container.innerHTML = '';
      const config = this.getBasicConfig();
      const certificates = this.buildMockCertificates(29);
      alisonGUI.renderCertificatesList(certificates, container, { gui: config.gui });
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    testCarouselTiming() {
      const container = this.$refs.carouselContainer;
      container.innerHTML = '';
      this.setCarouselWidth(866);
      container.style.display = 'none';

      const config = this.getBasicConfig();
      const certificates = this.buildMockCertificates(29);

      alisonGUI.renderCertificatesList(certificates, container, {
        gui: config.gui,
        onReady: (emblaApi) => {
          this.resultHtml = '<p>onReady: carrusel listo (emblaApi expuesto, ver consola) mientras el contenedor seguía oculto.</p>';
          console.log('[ALILIBS-331] emblaApi listo con el contenedor aún display:none:', emblaApi);
        },
      });

      requestAnimationFrame(() => {
        container.style.display = '';
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    sentMessageToPlugin() {
      window.postMessage({ action: 'sendData', payload: { message: 'Hola desde la página web externa' } }, '*');
    },
    testMessageToPlugin() {
      if (typeof window.getDataFromExtension !== 'function') {
        console.error('getDataFromExtension no está disponible (requiere el plugin de Alison Desktop instalado)');
        this.resultHtml = '<p>getDataFromExtension no está disponible (requiere el plugin de Alison Desktop instalado).</p>';
        return;
      }
      window.getDataFromExtension()
        .then((data) => console.log('Datos obtenidos:', data))
        .catch((error) => console.error('Error:', error));
    },
  },
};
</script>

<style scoped>
.test-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.format-row {
  margin-bottom: 6px;
  /* Los 4 botones de cada grupo (html/text/base64) van en una sola línea,
     como en index.html: acá se pisa el flex-wrap heredado de .test-buttons
     (los v-btn de Vuetify son más anchos que un <button> plano, así que con
     wrap se cortaban a la mitad del ancho típico del panel) y se deja
     scroll horizontal como salida en pantallas angostas. */
  flex-wrap: nowrap;
  overflow-x: auto;
}
.format-label {
  width: 52px;
  flex-shrink: 0;
  font-size: 12px;
  color: #666;
}
.palette-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}
.palette-card {
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.palette-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,.18); }
.palette-card.active { border-color: #333; }
.palette-swatch {
  height: 36px;
  display: flex;
}
.palette-swatch span { flex: 1; }
.palette-label {
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #f5f5f5;
  border-top: 1px solid #e0e0e0;
}
.result-box {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
  min-height: 50px;
  max-height: 300px;
  overflow-y: auto;
}
.carousel-container {
  box-sizing: border-box;
  border: 2px dashed #999;
  padding: 10px;
  transition: width 0.2s ease;
  max-width: 100%;
}
</style>
